import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Pause, Play, Ban, KeyRound, Pencil, Receipt, Loader2, FileText,
  Activity, AlertTriangle, Wrench, RefreshCw, PackageCheck, Plus, ArrowRightLeft,
  MapPin, QrCode, Package, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ActionDialog } from "@/components/ActionDialog";
import {
  useCancelarContrato, useContrato, useMarcarEquipoRecuperado, useReactivarContrato,
  useRegenerarPassword, useReintentarProvisionar, useSuspenderContrato,
} from "./api";
import { useListPromesas } from "@/features/promesas/api";
import { PromesaBadge } from "@/features/promesas/PromesaBadge";
import { ReconcileResultDialog, type ReconcileResult } from "@/components/ReconcileResultDialog";
import { toast } from "sonner";
import { useJobPolling } from "@/features/jobs/useJobPolling";
import { PasswordRevealDialog } from "./PasswordRevealDialog";
import { ContratoEditDialog } from "./ContratoEditDialog";
import { WifiCardDialog } from "./WifiCardDialog";
import { MoverContratoDialog } from "./MoverContratoDialog";
import { EditarUbicacionDialog } from "./EditarUbicacionDialog";
import { ContratoDocumentoSection } from "./ContratoDocumentoSection";
import { AuditTimeline } from "@/features/audit/AuditTimeline";
import { ContratoFacturasTab } from "@/features/facturas/ContratoFacturasTab";
import { PagosTable } from "@/features/pagos/PagosTable";
import { GenerarFacturaDialog } from "@/features/facturas/GenerarFacturaDialog";
import { EmitirServicioAdicionalDialog } from "@/features/facturas/EmitirServicioAdicionalDialog";
import { useBackLink } from "@/hooks/useBackLink";
import { useCliente } from "@/features/clientes/api";
import { usePlan } from "@/features/planes/api";
import { useRouter } from "@/features/routers/api";
import { useRed } from "@/features/redes/api";
import {
  CPE_VENDOR_LABEL, ONU_VENDOR_LABEL, type EstatusContrato, type RegenerarPasswordResponse,
} from "./schemas";
import { formatDate, formatMXN } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth";
import type { ApiError } from "@/lib/api";

const ESTATUS_VARIANT: Record<EstatusContrato, "success" | "warning" | "destructive" | "secondary" | "default"> = {
  activo: "success",
  suspendido: "warning",
  cancelado: "destructive",
  error: "destructive",
  pendiente: "secondary",
};

export function ContratoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackLink("/contratos", "Contratos");
  const { data: contrato, isLoading, isError } = useContrato(id ?? null);

  // rc34: permisos efectivos del user — para gate de botones admin.
  // El rol "cajero" tiene contratos.read pero NO contratos.write/suspender/cancel,
  // NI facturas.write. Esto oculta los botones aunque la ruta sea accesible.
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEditContrato = hasPermission("contratos.write");
  const canSuspendContrato = hasPermission("contratos.suspender");
  const canReactivateContrato = hasPermission("contratos.reactivar");
  const canCancelContrato = hasPermission("contratos.cancel");
  const canCreateFactura = hasPermission("facturas.write");
  // rc44: emitir Servicio adicional (factura libre) requiere permiso adicional admin
  const canCreateServicioAdicional = hasPermission("facturas.libre.create");
  const canRecuperarEquipo = hasPermission("contratos.equipo_recuperar");

  const [tab, setTab] = useState("general");
  // rc121 — Tarjeta WiFi (QR) del módem, en vivo desde ByteACS
  const [wifiCardOpen, setWifiCardOpen] = useState(false);
  const [suspenderOpen, setSuspenderOpen] = useState(false);
  const [cancelarOpen, setCancelarOpen] = useState(false);
  const [reactivarOpen, setReactivarOpen] = useState(false);
  const [regenerarOpen, setRegenerarOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [moverOpen, setMoverOpen] = useState(false);
  const [facturaOpen, setFacturaOpen] = useState(false);
  const [servicioAdicionalOpen, setServicioAdicionalOpen] = useState(false);
  // rc103 — modal de ubicación GPS sobre el mapa
  const [ubicacionOpen, setUbicacionOpen] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<RegenerarPasswordResponse | null>(null);
  const [reconcileResult, setReconcileResult] = useState<ReconcileResult | null>(null);
  const [pollingJobId, setPollingJobId] = useState<string | null>(null);

  const suspenderMut = useSuspenderContrato(id ?? "");
  const reactivarMut = useReactivarContrato(id ?? "");
  const cancelarMut = useCancelarContrato(id ?? "");
  const regenerarMut = useRegenerarPassword(id ?? "");
  const reintentarMut = useReintentarProvisionar(id ?? "");
  const equipoMut = useMarcarEquipoRecuperado(id ?? "");

  // F92 — promesa activa del cliente (solo para mostrar badge en header)
  const { data: promesasData } = useListPromesas(
    { cliente_id: contrato?.cliente_id, estado: "activa", limit: 1 },
    { enabled: Boolean(contrato?.cliente_id) },
  );
  const promesaActiva = promesasData?.items?.[0] ?? null;

  // Polling del job activo
  useJobPolling(pollingJobId, {
    invalidate: [["contratos"], ["clientes", "resumen"]],
    onComplete: (job) => {
      setPollingJobId(null);
      const res = (job.result ?? {}) as ReconcileResult;
      setReconcileResult({ ...res, success: true });
      toast.success("Operación completada", {
        description: res.anomalies?.length
          ? `${res.anomalies.length} anomalía(s) reportadas`
          : "Router consistente",
      });
    },
    onError: (job) => {
      setPollingJobId(null);
      setReconcileResult({
        success: false,
        action: job.action,
        anomalies: [],
        error: job.error ?? "Error desconocido",
      });
      toast.error("Operación falló", {
        description: job.error ?? job.error_code ?? "Ver detalles",
      });
    },
  });

  // Enriquecer con datos de las entidades relacionadas
  const { data: cliente } = useCliente(contrato?.cliente_id ?? null);
  const { data: plan } = usePlan(contrato?.plan_id ?? null);
  const { data: router } = useRouter(contrato?.router_id ?? null);
  const { data: red } = useRed(contrato?.red_id ?? null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  if (isError || !contrato) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <FileText size={32} className="text-muted-foreground" />
        <h2 className="text-base font-semibold">Contrato no encontrado</h2>
        <Button size="sm" variant="outline" onClick={() => navigate(back.path)}>
          <ArrowLeft size={14} /> Volver a {back.label}
        </Button>
      </div>
    );
  }

  // rc34: combina estado del contrato + permisos del user
  const canSuspend = contrato.estatus === "activo" && canSuspendContrato;
  const canReactivate =
    (contrato.estatus === "suspendido" || contrato.estatus === "error") && canReactivateContrato;
  const canCancel = contrato.estatus !== "cancelado" && canCancelContrato;
  const canRegenPass = contrato.estatus !== "cancelado" && canEditContrato;
  const canReintentar = contrato.estatus !== "cancelado" && canEditContrato;
  // F92 — Equipo: solo si modalidad=comodato, cancelado, y aún no recuperado.
  const equipoPendienteRecuperar =
    contrato.equipo_modalidad === "comodato" &&
    contrato.estatus === "cancelado" &&
    !contrato.equipo_recuperado_at;

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      {/* rc138 — móvil: título arriba, botonera en su propia fila */}
      <div className="flex items-start justify-between gap-4 max-md:flex-col max-md:gap-2">
        <div className="flex flex-col gap-1 max-md:min-w-0">
          <Link to={back.path} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground w-fit">
            <ArrowLeft size={12} /> {back.label}
          </Link>
          <div className="flex items-center gap-2 max-md:flex-wrap">
            <h1 className="text-lg font-semibold font-mono">{contrato.codigo}</h1>
            <Badge variant={ESTATUS_VARIANT[contrato.estatus]}>{contrato.estatus}</Badge>
            {contrato.exento_facturacion && (
              <Badge variant="warning" title="Cliente gratis — no se factura">
                exento facturación
              </Badge>
            )}
            {contrato.exento_monitoreo && (
              <Badge variant="secondary" title="Excluido de monitoreo ByteNMS">
                sin monitoreo
              </Badge>
            )}
            {/* F92/F108 — badges de equipo con color */}
            {contrato.equipo_modalidad === "comodato" && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900/40"
                title={contrato.equipo_descripcion || "Equipo en comodato — ISP recupera al cancelar"}
              >
                <Package size={10} /> Comodato
              </span>
            )}
            {contrato.equipo_modalidad === "propiedad_cliente" && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 ring-1 ring-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/40"
                title="Equipo propiedad del cliente"
              >
                <Check size={10} /> Propiedad del cliente
              </span>
            )}
            {equipoPendienteRecuperar && (
              <Badge variant="warning" title="Equipo en comodato pendiente de recoger">
                equipo por recuperar
              </Badge>
            )}
            {promesaActiva && <PromesaBadge promesa={promesaActiva} />}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            {cliente && (
              <Link to={`/clientes/${cliente.id}`} className="hover:text-foreground hover:underline">
                {cliente.codigo} — {cliente.razon_social}
              </Link>
            )}
            {plan && <><span>·</span><span>{plan.nombre}</span></>}
            {router && <><span>·</span><span>Router: {router.nombre}</span></>}
            {red && <><span>·</span><span className="font-mono">Red: {red.cidr}</span></>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Generar factura — botón principal verde cuando el contrato puede facturarse */}
          {contrato.estatus !== "cancelado" && !contrato.exento_facturacion && canCreateFactura && (
            <Button
              size="sm"
              onClick={() => setFacturaOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Receipt size={12} /> Generar factura
            </Button>
          )}
          {/* rc44: Servicio adicional (factura libre) — solo admin con permiso especial */}
          {contrato.estatus !== "cancelado" && canCreateServicioAdicional && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setServicioAdicionalOpen(true)}
              title="Cargo extra que NO cuenta para cobranza (cambio domicilio, equipo extra, etc.)"
            >
              <Plus size={12} /> Servicio adicional
            </Button>
          )}
          {contrato.estatus !== "cancelado" && canEditContrato && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil size={12} /> Editar
            </Button>
          )}
          {contrato.estatus !== "cancelado" && canEditContrato && !contrato.fiber_detalle && (
            <Button size="sm" variant="outline" onClick={() => setMoverOpen(true)}
              title="Cambio de domicilio: reasignar a otro router (borra secret viejo, recrea en el nuevo)">
              <ArrowRightLeft size={12} /> Mover de router
            </Button>
          )}
          {canSuspend && (
            <Button size="sm" variant="outline" onClick={() => setSuspenderOpen(true)}>
              <Pause size={12} /> Suspender
            </Button>
          )}
          {canReactivate && (
            <Button size="sm" variant="outline" onClick={() => setReactivarOpen(true)}>
              <Play size={12} /> Reactivar
            </Button>
          )}
          {canRegenPass && (
            <Button size="sm" variant="outline" onClick={() => setRegenerarOpen(true)}>
              <KeyRound size={12} /> Regenerar password
            </Button>
          )}
          {canReintentar && (
            <Button
              size="sm"
              variant={contrato.estatus === "error" ? "default" : "outline"}
              disabled={reintentarMut.isPending || pollingJobId !== null}
              onClick={async () => {
                try {
                  const res = await reintentarMut.mutateAsync();
                  if (res.job_id) {
                    setPollingJobId(res.job_id);
                    toast.info("Operación encolada", {
                      description: "Procesándose en segundo plano…",
                    });
                  }
                } catch (e) {
                  toast.error("Error al encolar", {
                    description: (e as ApiError).message,
                  });
                }
              }}
              title="Encola reconciliación del router: limpia residuos y recrea el secret limpio."
            >
              <RefreshCw size={12} className={pollingJobId ? "animate-spin" : ""} />
              {pollingJobId ? "Procesando…" : "Reintentar provisionar"}
            </Button>
          )}
          {equipoPendienteRecuperar && canRecuperarEquipo && (
            <Button
              size="sm"
              variant="outline"
              disabled={equipoMut.isPending}
              onClick={async () => {
                if (!confirm(`¿Marcar equipo "${contrato.equipo_descripcion || "comodato"}" como ya recogido?`)) return;
                try {
                  await equipoMut.mutateAsync(undefined);
                  toast.success("Equipo marcado como recuperado");
                } catch (e) {
                  toast.error("Error", { description: (e as ApiError).message });
                }
              }}
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
            >
              <PackageCheck size={12} /> Marcar equipo recuperado
            </Button>
          )}
          {canCancel && (
            <Button size="sm" variant="outline" onClick={() => setCancelarOpen(true)}>
              <Ban size={12} /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Card: IP/PPPoE + error del router si aplica */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 pt-4 md:grid-cols-4">
          <div>
            <div className="text-[10px] font-semibold uppercase text-muted-foreground">IP asignada</div>
            <div className="font-mono text-sm">{contrato.ip_asignada}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-muted-foreground">PPPoE user</div>
            <div className="font-mono text-sm">{contrato.pppoe_user}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-muted-foreground">Día de corte</div>
            <div className="text-sm">Día {contrato.fecha_corte} de cada mes</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase text-muted-foreground">Alta</div>
            <div className="text-sm">{formatDate(contrato.created_at)}</div>
          </div>
        </CardContent>
        {contrato.estatus === "error" && contrato.ultimo_error_router && (
          <CardContent className="border-t bg-destructive/5 pt-3">
            <div className="flex items-start gap-2 text-xs text-destructive">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>Error del router:</strong> {contrato.ultimo_error_router}
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Último intento: {contrato.ultimo_intento_router ? formatDate(contrato.ultimo_intento_router) : "—"} · Fallos consecutivos: {contrato.fallos_consecutivos}
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="general"><FileText size={12} className="mr-1" />General</TabsTrigger>
          <TabsTrigger value="facturas"><Receipt size={12} className="mr-1" />Facturas</TabsTrigger>
          <TabsTrigger value="pagos"><Receipt size={12} className="mr-1" />Pagos</TabsTrigger>
          <TabsTrigger value="historial"><Activity size={12} className="mr-1" />Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          {/* F18.1 — Documento del contrato */}
          <div className="mb-4">
            <ContratoDocumentoSection contrato={contrato} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Facturación</CardTitle></CardHeader>
              <CardContent className="text-xs">
                <dl className="grid grid-cols-[140px_1fr] gap-y-1.5">
                  <dt className="text-muted-foreground">Precio override</dt>
                  <dd>{contrato.precio_override ? formatMXN(contrato.precio_override) : <em className="text-muted-foreground">usa precio del plan</em>}</dd>
                  <dt className="text-muted-foreground">Descuento %</dt>
                  <dd>{contrato.descuento_pct}%</dd>
                  <dt className="text-muted-foreground">Días gracia override</dt>
                  <dd>{contrato.dias_gracia_override ?? <em className="text-muted-foreground">default sistema</em>}</dd>
                  {/* rc90 — facturación automática estando suspendido */}
                  <dt className="text-muted-foreground">Suspendido factura</dt>
                  <dd>
                    {contrato.facturar_suspendido
                      ? "Sí — sigue facturando suspendido"
                      : <em className="text-muted-foreground">no — al suspenderse deja de facturar</em>}
                  </dd>
                  {/* rc90 — reinicio de ciclo (solo se muestra si está seteado y es futuro) */}
                  {contrato.facturar_desde && new Date(contrato.facturar_desde) > new Date() && (
                    <>
                      <dt className="text-muted-foreground">Factura desde</dt>
                      <dd className="font-mono">{contrato.facturar_desde}</dd>
                    </>
                  )}
                </dl>
                {contrato.exento_facturacion ? (
                  <div className="mt-3 border-t pt-3 text-[11px] text-muted-foreground">
                    Contrato exento — no se emiten facturas.
                  </div>
                ) : contrato.estatus === "cancelado" ? (
                  <div className="mt-3 border-t pt-3 text-[11px] text-muted-foreground">
                    Contrato cancelado — no se puede facturar.
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Wireless / Fiber detalle */}
            {contrato.wireless_detalle && (
              <Card>
                <CardHeader><CardTitle><Wrench size={12} className="inline mr-1" />Detalle wireless</CardTitle></CardHeader>
                <CardContent className="text-xs">
                  <WirelessDetalleView data={contrato.wireless_detalle} />
                </CardContent>
              </Card>
            )}
            {contrato.fiber_detalle && (
              <Card>
                <CardHeader className="flex-row items-center justify-between space-y-0">
                  <CardTitle><Wrench size={12} className="inline mr-1" />Detalle fibra</CardTitle>
                  {/* rc121 — tarjeta QR de la WiFi (SSID + clave desde ByteACS) */}
                  {contrato.fiber_detalle.onu_serial && (
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => setWifiCardOpen(true)}
                    >
                      <QrCode size={13} className="mr-1" />Tarjeta WiFi
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="text-xs">
                  <FiberDetalleView data={contrato.fiber_detalle} />
                </CardContent>
              </Card>
            )}

            {/* rc101/rc103 — Ubicación GPS de la instalación (por contrato).
                Siempre visible: con GPS muestra datos + Editar; sin GPS
                invita a marcarla en el mapa. Cambios auditados (F17.3). */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span><MapPin size={12} className="inline mr-1" />Ubicación</span>
                  {canEditContrato && contrato.estatus !== "cancelado" && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => setUbicacionOpen(true)}
                    >
                      <Pencil size={12} className="mr-1" />
                      {contrato.latitud != null ? "Editar" : "Marcar en el mapa"}
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs">
                {contrato.latitud != null && contrato.longitud != null ? (
                  <>
                    <dl className="grid grid-cols-[140px_1fr] gap-y-1.5">
                      <dt className="text-muted-foreground">Coordenadas</dt>
                      <dd className="font-mono">
                        {Number(contrato.latitud).toFixed(6)}, {Number(contrato.longitud).toFixed(6)}
                      </dd>
                      {contrato.ubicacion_referencia && (
                        <>
                          <dt className="text-muted-foreground">Referencia</dt>
                          <dd>{contrato.ubicacion_referencia}</dd>
                        </>
                      )}
                    </dl>
                    <a
                      href={`https://maps.google.com/?q=${contrato.latitud},${contrato.longitud}`}
                      target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <MapPin size={11} /> Abrir en Google Maps
                    </a>
                  </>
                ) : (
                  <div className="text-muted-foreground italic">
                    Sin ubicación registrada — márcala en el mapa para que
                    aparezca en el módulo Mapa y los técnicos puedan navegar al domicilio.
                  </div>
                )}
              </CardContent>
            </Card>

            {contrato.notas && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle>Notas</CardTitle></CardHeader>
                <CardContent className="text-xs">{contrato.notas}</CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="facturas">
          <ContratoFacturasTab contratoId={contrato.id} fromLabel={contrato.codigo} />
        </TabsContent>
        <TabsContent value="pagos">
          <PagosTable
            filters={{ contrato_id: contrato.id, incluir_anulados: true, size: 50 }}
            hideClienteColumn
          />
        </TabsContent>
        <TabsContent value="historial">
          <AuditTimeline entidad="contrato" entidadId={contrato.id} />
        </TabsContent>
      </Tabs>

      {/* ── Dialogs de acciones ── */}
      <ActionDialog
        open={suspenderOpen}
        onOpenChange={setSuspenderOpen}
        title="Suspender contrato"
        description="Se deshabilita el PPPoE en el router. El cliente pierde acceso hasta que se reactive. Se encola y procesa en segundo plano."
        actionLabel="Suspender"
        isPending={suspenderMut.isPending}
        motivoRequired={false}
        onConfirm={async (motivo) => {
          try {
            const res = await suspenderMut.mutateAsync(motivo);
            setSuspenderOpen(false);
            if (res.job_id) {
              setPollingJobId(res.job_id);
              toast.info("Suspensión encolada", { description: "Aplicándose al router…" });
            }
          } catch (e) { return (e as ApiError).message; }
        }}
      />
      <ActionDialog
        open={reactivarOpen}
        onOpenChange={setReactivarOpen}
        title="Reactivar contrato"
        description="Se habilita el PPPoE en el router. El cliente recupera acceso. Se encola y procesa en segundo plano."
        actionLabel="Reactivar"
        isPending={reactivarMut.isPending}
        motivoRequired={false}
        onConfirm={async (motivo) => {
          try {
            const res = await reactivarMut.mutateAsync(motivo);
            setReactivarOpen(false);
            if (res.job_id) {
              setPollingJobId(res.job_id);
              toast.info("Reactivación encolada", { description: "Aplicándose al router…" });
            }
          } catch (e) { return (e as ApiError).message; }
        }}
      />
      <ActionDialog
        open={cancelarOpen}
        onOpenChange={setCancelarOpen}
        title="Cancelar contrato"
        description={
          contrato.equipo_modalidad === "comodato"
            ? `⚠️ Equipo en COMODATO — recuperar al cancelar: ${contrato.equipo_descripcion || "(sin descripción)"}. Acción irreversible: el contrato queda cancelado y se encola limpieza del router.`
            : "Acción irreversible. El contrato queda cancelado y se encola limpieza del router (secret + active + address-list)."
        }
        actionLabel="Cancelar contrato"
        actionVariant="destructive"
        isPending={cancelarMut.isPending}
        motivoRequired
        extraCheckbox={
          contrato.equipo_modalidad === "comodato"
            ? {
                key: "equipo_recuperado_ahora",
                label: `Marcar equipo como ya recogido ahora mismo (${contrato.equipo_descripcion || "comodato"})`,
                defaultChecked: false,
              }
            : undefined
        }
        onConfirm={async (motivo, extras) => {
          if (!motivo) return "Motivo requerido para cancelar";
          try {
            const res = await cancelarMut.mutateAsync({
              motivo,
              equipo_recuperado_ahora: Boolean(extras?.equipo_recuperado_ahora),
            });
            setCancelarOpen(false);
            if (res.job_id) {
              toast.info("Cancelación encolada", {
                description: "Limpiándose en el router…",
              });
            }
          } catch (e) { return (e as ApiError).message; }
        }}
      />

      {/* Regenerar password */}
      <Dialog open={regenerarOpen && !revealedPassword} onOpenChange={setRegenerarOpen}>
        <DialogHeader>
          <DialogTitle>Regenerar password PPPoE</DialogTitle>
          <DialogDescription>
            Se genera una nueva password y se aplica al secret del router. El técnico tendrá que
            re-configurar el CPE con la nueva password.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <p className="text-xs text-muted-foreground">
            Usuario PPPoE: <span className="font-mono">{contrato.pppoe_user}</span>
          </p>
        </DialogContent>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setRegenerarOpen(false)}>Cancelar</Button>
          <Button
            size="sm"
            disabled={regenerarMut.isPending}
            onClick={async () => {
              try {
                const resp = await regenerarMut.mutateAsync();
                setRevealedPassword(resp);
              } catch (e) {
                alert(`Error: ${(e as ApiError).message}`);
              }
            }}
          >
            {regenerarMut.isPending ? "Generando…" : "Regenerar"}
          </Button>
        </DialogFooter>
      </Dialog>

      {revealedPassword && (
        <PasswordRevealDialog
          open={true}
          onConfirm={() => { setRevealedPassword(null); setRegenerarOpen(false); }}
          pppoeUser={revealedPassword.pppoe_user}
          password={revealedPassword.pppoe_password_plain}
          title="Password regenerado"
          subtitle={revealedPassword.mensaje}
        />
      )}

      <ReconcileResultDialog
        open={reconcileResult !== null}
        onOpenChange={(v) => { if (!v) setReconcileResult(null); }}
        result={reconcileResult}
        title="Reprovisionar contrato en router"
      />

      {/* Editar contrato (F17.2) */}
      <ContratoEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contrato={contrato}
      />

      <MoverContratoDialog
        open={moverOpen}
        onOpenChange={setMoverOpen}
        contrato={contrato}
      />

      {/* rc103 — ubicación GPS sobre el mapa (click/arrastrar, auditado) */}
      {ubicacionOpen && (
        <EditarUbicacionDialog
          open={ubicacionOpen}
          onOpenChange={setUbicacionOpen}
          contrato={contrato}
        />
      )}

      {/* Generar factura manual (F20.2) */}
      <GenerarFacturaDialog
        open={facturaOpen}
        onOpenChange={setFacturaOpen}
        contrato={contrato}
        clienteRazonSocial={cliente?.razon_social}
        planNombre={plan?.nombre}
        planPrecio={plan?.precio}
      />

      {/* rc44 — Emitir Servicio adicional (factura libre, no cuenta para cobranza) */}
      <EmitirServicioAdicionalDialog
        open={servicioAdicionalOpen}
        onOpenChange={setServicioAdicionalOpen}
        contrato={contrato}
        clienteRazonSocial={cliente?.razon_social}
      />

      {/* rc121 — Tarjeta WiFi (QR): SSID + clave en vivo desde ByteACS,
          compartible/guardable en galería desde el móvil (Web Share) */}
      {contrato.fiber_detalle?.onu_serial && wifiCardOpen && (
        <WifiCardDialog
          open={wifiCardOpen}
          onOpenChange={setWifiCardOpen}
          serial={contrato.fiber_detalle.onu_serial}
          clienteNombre={cliente?.razon_social}
          empresaId={cliente?.empresa_id}
        />
      )}

    </div>
  );
}

// ── Views para wireless/fiber detalle ──
function WirelessDetalleView({ data }: { data: NonNullable<import("./schemas").WirelessDetalleOut> }) {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-y-1">
      {data.cpe_vendor && <><dt className="text-muted-foreground">CPE vendor</dt><dd>{CPE_VENDOR_LABEL[data.cpe_vendor]}</dd></>}
      {data.cpe_modelo && <><dt className="text-muted-foreground">Modelo</dt><dd>{data.cpe_modelo}</dd></>}
      {data.cpe_mac && <><dt className="text-muted-foreground">MAC</dt><dd className="font-mono">{data.cpe_mac}</dd></>}
      {!data.cpe_mac && data.cpe_mac_historica && <><dt className="text-muted-foreground">MAC (histórica)</dt><dd className="font-mono text-muted-foreground" title="MAC que tuvo el equipo antes de cancelar; se liberó para reuso">{data.cpe_mac_historica}</dd></>}
      {data.cpe_serial && <><dt className="text-muted-foreground">Serial</dt><dd className="font-mono">{data.cpe_serial}</dd></>}
      {data.cpe_ip_management && <><dt className="text-muted-foreground">IP mgmt</dt><dd className="font-mono">{data.cpe_ip_management}</dd></>}
      {data.ssid_conexion && <><dt className="text-muted-foreground">SSID</dt><dd>{data.ssid_conexion}</dd></>}
      {data.frecuencia_mhz && <><dt className="text-muted-foreground">Frecuencia</dt><dd>{data.frecuencia_mhz} MHz</dd></>}
      {data.polarizacion && <><dt className="text-muted-foreground">Polarización</dt><dd>{data.polarizacion}</dd></>}
      {data.cpe_usuario_admin && <><dt className="text-muted-foreground">Admin user</dt><dd>{data.cpe_usuario_admin}</dd></>}
      {data.notas_instalacion && <><dt className="text-muted-foreground">Notas</dt><dd>{data.notas_instalacion}</dd></>}
    </dl>
  );
}

function FiberDetalleView({ data }: { data: NonNullable<import("./schemas").FiberDetalleOut> }) {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-y-1">
      {data.onu_vendor && <><dt className="text-muted-foreground">ONU vendor</dt><dd>{ONU_VENDOR_LABEL[data.onu_vendor]}</dd></>}
      {data.onu_modelo && <><dt className="text-muted-foreground">Modelo</dt><dd>{data.onu_modelo}</dd></>}
      {data.onu_serial && <><dt className="text-muted-foreground">Serial</dt><dd className="font-mono">{data.onu_serial}</dd></>}
      {data.onu_mac && <><dt className="text-muted-foreground">MAC</dt><dd className="font-mono">{data.onu_mac}</dd></>}
      {!data.onu_mac && data.onu_mac_historica && <><dt className="text-muted-foreground">MAC (histórica)</dt><dd className="font-mono text-muted-foreground" title="MAC que tuvo la ONU antes de cancelar; se liberó para reuso">{data.onu_mac_historica}</dd></>}
      {data.onu_ip_management && <><dt className="text-muted-foreground">IP mgmt</dt><dd className="font-mono">{data.onu_ip_management}</dd></>}
      {data.olt_puerto && <><dt className="text-muted-foreground">Puerto OLT</dt><dd className="font-mono">{data.olt_puerto}</dd></>}
      {data.splitter_nivel && <><dt className="text-muted-foreground">Splitter</dt><dd>{data.splitter_nivel}</dd></>}
      {data.potencia_optica_dbm_esperada && <><dt className="text-muted-foreground">Potencia óptica</dt><dd>{data.potencia_optica_dbm_esperada} dBm</dd></>}
      {data.distancia_m_estimada && <><dt className="text-muted-foreground">Distancia</dt><dd>{data.distancia_m_estimada} m</dd></>}
      {data.notas_instalacion && <><dt className="text-muted-foreground">Notas</dt><dd>{data.notas_instalacion}</dd></>}
    </dl>
  );
}
